import { gql, useQuery } from '@apollo/client';
import { SuperAdmin } from './superAdmins';

export enum HealthLevel {
  Red = 'Red',
  Yellow = 'Yellow',
  Green = 'Green',
}

export enum HealthReasonType {
  InventorySynced = 'InventorySynced',
  HasInventory = 'HasInventory',
  HasRecentPosts = 'HasRecentPosts',
  ListingToGroups = 'ListingToGroups',
  ListEverythingForEveryoneEnabled = 'ListEverythingForEveryoneEnabled',
  UserTokenHealth = 'UserTokenHealth',
  NumberOfActiveUsers = 'NumberOfActiveUsers',
  HadThirtyDayCall = 'ThirtyDayCall',
}

export interface LocationHealthSummary {
  id: string;
  createdAt: Date;
  updatedAt: Date;
  locationId: string;
  locationName: string;
  organizationId: string;
  organizationName: string;
  healthLevel: HealthLevel;
  notes?: string;
  snoozedUntil?: Date;
  dismissedAt?: Date;
  lastRefreshedAt?: Date;
  lastRefreshStatus?: Date;
  assignedUser?: SuperAdmin;
  healthReasons: HealthReason[];
  poc?: PointOfContact;
  postingUsers?: PostingUser[];
}

export interface HealthReason {
  id: string;
  severity: string;
  type: string;
  name: string;
  description: string;
  dismissible: boolean;
  dismissedAt: string;
}

export interface PointOfContact {
  name: string;
  email: string;
  phone: string;
}

export interface PostingUser {
  id: string;
  name: string;
  token: string;
  profileId?: string;
  lastPostAt?: string;
  email?: string;
  phone?: string;
  locationId?: string;
  healthStatus?: {
    health: string;
    reasons: string[];
    updatedAt: string;
  };
  jobs: {
    id: string;
    jobType: string;
    createdAt: string;
    status?: string;
    failedReason?: string;
    htmlScreenshotUrl?: string;
    extensionVersion?: string;
    userAgent?: string;
    currentStep?: string;
  }[];
}

export interface LocationHealthSummaryResponse {
  locationHealthSummaries: LocationHealthSummary[];
}
export interface LocationsHealthSummaryParams {
  partnersOnly?: boolean;
  search?: string;
}

export const LOCATIONS_HEALTH_SUMMARIES_NAME = 'locationHealthSummaries';
export const LOCATION_HEALTH_SUMMARIES = gql`
    query ${LOCATIONS_HEALTH_SUMMARIES_NAME}($search: String, $partnersOnly: Boolean) {
    ${LOCATIONS_HEALTH_SUMMARIES_NAME}(search: $search, partnersOnly: $partnersOnly) {
      id
      createdAt
      updatedAt
      locationId
      locationName
      organizationId
      organizationName
      healthLevel
      notes
      snoozedUntil
      dismissedAt
      lastRefreshedAt
      lastRefreshStatus
      assignedUser {
        id
        username
        fullName
      }
  }
}
`;
export function useLocationHealthSummaryQuery({ search, partnersOnly }: { partnersOnly?: boolean; search?: string }) {
  const { data, error, loading, refetch } = useQuery<LocationHealthSummaryResponse, LocationsHealthSummaryParams>(
    LOCATION_HEALTH_SUMMARIES,
    {
      variables: { search, partnersOnly },
    }
  );
  return {
    locationHealthSummaries: data?.locationHealthSummaries || [],
    error,
    loading,
    refetch,
  };
}

export const POSTING_USERS = gql`
  query allPostingUsers {
    allPostingUsers {
      id
      name
      token
      profileId
      locationId
      lastPostAt
      email
      phone
      healthStatus {
        health
        reasons
        updatedAt
      }
    }
  }
`;

export interface PostingUsersResponse {
  allPostingUsers: PostingUser[];
}

export interface PostingUsersParams {}

export function usePostingUsersQuery() {
  const { data, error, loading, refetch } = useQuery<PostingUsersResponse, PostingUsersParams>(POSTING_USERS, {
    variables: {},
  });
  return {
    postingUsers: data?.allPostingUsers || [],
    error,
    loading,
    refetch,
  };
}
